import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, Users, Phone, MapPin, Wallet, FileText } from 'lucide-react';
import { customersApi } from '../api';
import { formatPrice } from '../store';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { PaymentDialog, PaymentMethod } from '../components/ui/PaymentDialog';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { CustomerStatement } from './CustomerStatement';
import type { Customer } from '../types';

interface CustomerFormData {
  name: string;
  phone: string;
  address: string;
  notes: string;
  is_active: boolean;
}

const initialFormData: CustomerFormData = {
  name: '',
  phone: '',
  address: '',
  notes: '',
  is_active: true,
};

export const Customers: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDebtOnly, setShowDebtOnly] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [payingCustomer, setPayingCustomer] = useState<Customer | null>(null);
  // Qarz qaysi buyurtmalardan yig'ilganini ko'rsatadigan kartochka.
  const [statementCustomer, setStatementCustomer] = useState<Customer | null>(null);
  const [payingSaving, setPayingSaving] = useState(false);

  const handlePayDebt = async (amount: number, method: PaymentMethod) => {
    if (!payingCustomer) return;
    setPayingSaving(true);
    try {
      const res = await customersApi.payDebt(payingCustomer.id, { amount, method });
      setPayingCustomer(null);
      toast.success(
        `${formatPrice(amount)} UZS qabul qilindi. Qolgan qarz: ` +
        `${formatPrice(Number(res.data.remaining_debt))} UZS`,
      );
      fetchCustomers();
    } catch (error: any) {
      const data = error.response?.data;
      const msg = data?.amount?.[0] || data?.detail || "To'lovni saqlashda xato";
      toast.error(msg);
    } finally {
      setPayingSaving(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      if (showDebtOnly) {
        const res = await customersApi.withDebt();
        const data = Array.isArray(res.data) ? res.data : [];
        setCustomers(data);
        setTotalPages(1);
      } else {
        const params: Record<string, any> = { page };
        if (search) params.search = search;

        const res = await customersApi.list(params);
        const rawData = res.data as any;
        const data = Array.isArray(rawData) ? rawData : (rawData.results || []);
        const count = Array.isArray(rawData) ? rawData.length : (rawData.count || 0);
        setCustomers(data);
        setTotalPages(Math.ceil(count / 50) || 1);
      }
    } catch (error) {
      console.error('Customers fetch error:', error);
      toast.error('Mijozlarni yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, showDebtOnly]);

  const searchMounted = React.useRef(false);
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = async (id: number) => {
    try {
      const res = await customersApi.get(id);
      const c = res.data;
      setFormData({
        name: c.name,
        phone: c.phone,
        address: c.address || '',
        notes: c.notes || '',
        is_active: c.is_active,
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error('Mijozni yuklashda xato');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingId) {
        await customersApi.update(editingId, formData);
        toast.success('Mijoz yangilandi');
      } else {
        await customersApi.create(formData);
        toast.success('Mijoz qo\'shildi');
      }
      closeModal();
      fetchCustomers();
    } catch (error: any) {
      console.error('Save error:', error);
      const msg = error.response?.data?.detail || 'Saqlashda xato yuz berdi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: "Mijozni o'chirish",
      description: `"${name}" mijozini o'chirmoqchimisiz? Bu amalni ortga qaytarib bo'lmaydi.`,
      confirmLabel: "O'chirish",
      danger: true,
    });
    if (!confirmed) return;

    setDeleting(id);
    try {
      await customersApi.delete(id);
      toast.success("Mijoz o'chirildi");
      fetchCustomers();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("O'chirishda xato yuz berdi");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Mijozlar</h1>
        <button onClick={openAddModal} className="btn btn-primary flex items-center gap-2">
          <Plus size={20} />
          <span>Yangi mijoz</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Qidirish (ism, telefon)..."
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={showDebtOnly}
              onChange={(e) => {
                setShowDebtOnly(e.target.checked);
                setPage(1);
              }}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Faqat qarzdorlar</span>
          </label>
        </div>
      </div>

      {/* Customers list */}
      <div className="card overflow-hidden !p-0">
        {loading ? (
          <SkeletonRows />
        ) : customers.length === 0 ? (
          <EmptyState icon={Users} title="Mijoz topilmadi" />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ism</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Telefon</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Manzil</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Buyurtmalar</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Jami xarid</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qarz</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium">{customer.name}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone size={14} />
                          {customer.phone}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {customer.address ? (
                          <div className="flex items-center gap-1">
                            <MapPin size={14} />
                            <span className="truncate max-w-[150px]">{customer.address}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">{customer.total_orders}</td>
                      <td className="py-3 px-4 text-right font-medium tabular-nums whitespace-nowrap">
                        {formatPrice(Number(customer.total_spent))} UZS
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums whitespace-nowrap">
                        {Number(customer.debt) > 0 ? (
                          <span className="text-danger-600 font-medium">
                            {formatPrice(Number(customer.debt))} UZS
                          </span>
                        ) : (
                          <span className="text-success-600">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => setStatementCustomer(customer)}
                            title="Hisob varaqasi"
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                          >
                            <FileText size={18} />
                          </button>
                          {Number(customer.debt) > 0 && (
                            <button
                              onClick={() => setPayingCustomer(customer)}
                              title="Qarzni to'lash"
                              className="p-2 text-success-600 hover:bg-success-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500"
                            >
                              <Wallet size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(customer.id)}
                            className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(customer.id, customer.name)}
                            disabled={deleting === customer.id}
                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-100">
              {customers.map((customer) => (
                <div key={customer.id} className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{customer.name}</div>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                      <Phone size={13} className="shrink-0" />
                      <span className="truncate">{customer.phone}</span>
                    </div>
                    {customer.address && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <MapPin size={12} />
                        <span className="truncate">{customer.address}</span>
                      </div>
                    )}
                    {/* Summa va soni hech qachon o'rtasidan bo'linmasin — tor ekranda
                        `Qarz: 172 000 UZS` uch qatorga tushib ketardi. */}
                    <div className="flex items-center gap-3 mt-2 text-sm">
                      <span className="text-gray-500 whitespace-nowrap">
                        {customer.total_orders} buyurtma
                      </span>
                      {Number(customer.debt) > 0 ? (
                        <span className="text-danger-600 font-medium whitespace-nowrap">
                          Qarz: {formatPrice(Number(customer.debt))} UZS
                        </span>
                      ) : (
                        <span className="text-success-600 whitespace-nowrap">Qarzi yo'q</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setStatementCustomer(customer)}
                      title="Hisob varaqasi"
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                    >
                      <FileText size={18} />
                    </button>
                    {/* Qarzi yo'q mijozda ham joyi band turadi — aks holda ro'yxatning
                        o'ng cheti qatordan qatorga siljib, tishli bo'lib ko'rinadi. */}
                    {Number(customer.debt) > 0 ? (
                      <button
                        onClick={() => setPayingCustomer(customer)}
                        title="Qarzni to'lash"
                        className="p-2 text-success-600 hover:bg-success-50 rounded-lg"
                      >
                        <Wallet size={18} />
                      </button>
                    ) : (
                      <span className="w-[34px] shrink-0" aria-hidden />
                    )}
                    <button
                      onClick={() => openEditModal(customer.id)}
                      className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(customer.id, customer.name)}
                      disabled={deleting === customer.id}
                      className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!showDebtOnly && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-100">
            <button
              className="btn btn-secondary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Oldingi
            </button>
            <span className="px-4 text-gray-600 text-sm">
              {page} / {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Keyingi
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingId ? 'Mijozni tahrirlash' : 'Yangi mijoz'}
        size="sm"
        footer={
          <>
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" form="customer-form" disabled={saving} className="btn btn-primary">
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </>
        }
      >
        <form id="customer-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Ism *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Telefon *</label>
            <input
              type="tel"
              className="input"
              placeholder="+998901234567"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Manzil</label>
            <input
              type="text"
              className="input"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div>
            <label className="label">Izoh</label>
            <textarea
              className="input"
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Faol</span>
          </label>
        </form>
      </Modal>

      {/* Hisob varaqasi — qarz qaysi buyurtmalardan yig'ilgani */}
      {statementCustomer && (
        <CustomerStatement
          customer={statementCustomer}
          onClose={() => setStatementCustomer(null)}
          onPayDebt={() => {
            setPayingCustomer(statementCustomer);
            setStatementCustomer(null);
          }}
        />
      )}

      {/* Qarzni to'lash — pul eng eski buyurtmadan boshlab taqsimlanadi */}
      {payingCustomer && (
        <PaymentDialog
          open
          title={`${payingCustomer.name} — qarzni to'lash`}
          hint="Jami qarz"
          maxAmount={Number(payingCustomer.debt)}
          saving={payingSaving}
          onClose={() => setPayingCustomer(null)}
          onSubmit={handlePayDebt}
        />
      )}
    </div>
  );
};
